import { gql, useQuery } from '@apollo/client'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistGachasQuery,
  ArtistGachasQueryVariables,
  CurrentUserFragmentDoc,
  GachaBoxFragmentDoc,
  GachasConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    ArtistGachasQuery,
    ArtistGachasQueryVariables
  >({
    query: FETCH_NEW_GACHA,
    variables: { gachasArtistId: artistId },
    fields: [
      {
        fieldName: 'gachas',
        fragmentName: 'GachaBox',
        fragment: GachaBoxFragmentDoc,
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, loading, fetchMore } = useQuery<
    ArtistGachasQuery,
    ArtistGachasQueryVariables
  >(QUERY, {
    variables: { gachasArtistId: artistId },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted({ artist }) {
      isFirstLoadRef.current = false
      if (artist) {
        currentArtistVar(artist)
      }
    },
  })

  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      artistId,
      artistName: data?.artist?.displayName || '',
      hasNextPage: !!data?.gachas?.pageInfo.hasNextPage,
      artistGachas: data?.gachas ?? null,
      liveStreamId: data?.artist?.currentLive?.id,
      user: data?.user ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            gachasArtistId: artistId,
            gachasPageInput: {
              cursor: data?.gachas?.pageInfo.endCursor || null,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistGachas($gachasArtistId: ID, $gachasPageInput: PageInputDate) {
    user {
      ...CurrentUser
    }
    artist(id: $gachasArtistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    gachas(artistId: $gachasArtistId, pageInput: $gachasPageInput) {
      ...GachasConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${GachasConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_GACHA = gql`
  query FetchNewArtistGachas(
    $gachasArtistId: ID
    $gachasPageInput: PageInputDate
  ) {
    artist(id: $gachasArtistId) {
      ...ArtistBottomTab
      id
      displayName
      currentLive {
        id
      }
    }
    gachas(artistId: $gachasArtistId, pageInput: $gachasPageInput) {
      ...GachasConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${GachasConnectionFragmentDoc}
`
